import { notify } from "@general-backoffice/core";
import type { User } from "@investhinks/api-client";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  CustomInput,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { usersEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";

interface ManualBillingModalProps {
  isOpen: boolean;
  toggle: () => void;
  user: User | null;
  onSuccess?: () => void;
}

const ManualBillingModal: React.FC<ManualBillingModalProps> = ({ isOpen, toggle, user, onSuccess }) => {
  const { t } = useTranslation("users", { keyPrefix: "manualBilling" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");

  const userId = user?.id;
  const displayName = user?.name || user?.email || `User #${userId}`;
  const isCurrentlyManual = !!user?.isManualPayment;
  const currentReason = user?.manualPaymentReason || "";

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setConfirmed(false);
      setReason(isCurrentlyManual ? currentReason : "");
    }
  }, [isOpen, isCurrentlyManual, currentReason]);

  const handleSave = async () => {
    if (!confirmed || !userId) return;

    setIsLoading(true);
    setError(null);

    try {
      await usersEndpoints.updateUser(userId, {
        isManualPayment: !isCurrentlyManual,
        manualPaymentReason: !isCurrentlyManual ? reason : undefined,
      });
      notify.success(String(isCurrentlyManual ? t("disabledSuccess") : t("enabledSuccess")));
      onSuccess?.();
    } catch (err: unknown) {
      console.error("Failed to update manual billing:", err);
      setError(getErrorMessage(err, t("errorGeneric")));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} style={styles.modalHeader}>
        <div style={styles.headerContent}>
          <i
            className={`fas fa-${isCurrentlyManual ? "credit-card" : "file-invoice-dollar"}`}
            style={{
              ...styles.headerIcon,
              color: isCurrentlyManual ? "#10b981" : "#f59e0b",
            }}
          />
          <span>{isCurrentlyManual ? t("titleDisable") : t("titleEnable")}</span>
        </div>
      </ModalHeader>

      <ModalBody style={styles.modalBody}>
        {/* User Info */}
        <div style={styles.userInfo}>
          <div style={styles.userName}>{displayName}</div>
          {isCurrentlyManual && (
            <Badge color="secondary" style={styles.badge}>
              {t("manualPayment")}
            </Badge>
          )}
        </div>

        {/* Current Status */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("currentStatus")}</div>
          <div style={styles.statusCard}>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>{t("billingMode")}:</span>
              <span style={isCurrentlyManual ? styles.statusManual : styles.statusAuto}>
                {isCurrentlyManual ? t("manual") : t("automatic")}
              </span>
            </div>
            {isCurrentlyManual && currentReason && (
              <div style={styles.statusRow}>
                <span style={styles.statusLabel}>{t("reason")}:</span>
                <span style={styles.statusValue}>{currentReason}</span>
              </div>
            )}
          </div>
        </div>

        {/* What will change */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>{t("whatWillChange")}</div>
          {isCurrentlyManual ? (
            <Alert color="success" style={styles.infoAlert}>
              <div style={styles.alertHeader}>
                <i className="fas fa-credit-card" style={styles.alertIconSuccess} />
                <strong>{t("switchToAutomatic")}</strong>
              </div>
              <ul style={styles.changesList}>
                <li>{t("autoChange1")}</li>
                <li>{t("autoChange2")}</li>
                <li>{t("autoChange3")}</li>
              </ul>
            </Alert>
          ) : (
            <Alert color="warning" style={styles.infoAlert}>
              <div style={styles.alertHeader}>
                <i className="fas fa-file-invoice-dollar" style={styles.alertIconWarning} />
                <strong>{t("switchToManual")}</strong>
              </div>
              <ul style={styles.changesList}>
                <li>{t("manualChange1")}</li>
                <li>{t("manualChange2")}</li>
                <li>{t("manualChange3")}</li>
                <li>{t("manualChange4")}</li>
              </ul>
            </Alert>
          )}
        </div>

        {/* Reason field (only when enabling manual) */}
        {!isCurrentlyManual && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>{t("reasonLabel")}</div>
            <Input
              type="textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("reasonPlaceholder")}
              style={styles.textarea}
              rows={2}
            />
            <div style={styles.reasonHint}>{t("reasonHint")}</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.errorBanner}>
            <i className="fas fa-exclamation-circle" style={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div style={styles.section}>
          <CustomInput
            type="checkbox"
            id="confirmManualBilling"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label={isCurrentlyManual ? t("confirmDisable") : t("confirmEnable")}
            style={styles.checkbox}
          />
        </div>
      </ModalBody>

      <ModalFooter style={styles.modalFooter}>
        <Button color="secondary" outline onClick={toggle} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <Button
          color={isCurrentlyManual ? "success" : "warning"}
          onClick={handleSave}
          disabled={!confirmed || isLoading}
          style={styles.saveButton}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("saving")}
            </>
          ) : (
            <>
              <i className={`fas fa-${isCurrentlyManual ? "credit-card" : "file-invoice-dollar"} me-2`} />
              {isCurrentlyManual ? t("enableAutomatic") : t("enableManual")}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const styles = {
  modalHeader: {
    borderBottom: "1px solid #e5e7eb",
    padding: "16px 20px",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    fontSize: 18,
  },
  modalBody: {
    padding: "20px",
    maxHeight: "70vh",
    overflowY: "auto",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: "1px solid #e5e7eb",
  },
  userName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#111827",
  },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statusCard: {
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusLabel: {
    fontSize: 13,
    color: "#6b7280",
  },
  statusValue: {
    fontSize: 13,
    color: "#374151",
  },
  statusManual: {
    fontSize: 13,
    fontWeight: 500,
    color: "#f59e0b",
  },
  statusAuto: {
    fontSize: 13,
    fontWeight: 500,
    color: "#10b981",
  },
  infoAlert: {
    marginBottom: 0,
  },
  alertHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  alertIconWarning: {
    color: "#d97706",
  },
  alertIconSuccess: {
    color: "#059669",
  },
  changesList: {
    margin: 0,
    paddingLeft: 20,
    fontSize: 13,
  },
  textarea: {
    padding: "10px 12px",
    fontSize: 14,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    resize: "vertical",
  },
  reasonHint: {
    marginTop: 8,
    fontSize: 12,
    color: "#6b7280",
  },
  errorBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    borderRadius: 8,
    fontSize: 13,
    color: "#991b1b",
    marginBottom: 20,
  },
  errorIcon: {
    color: "#ef4444",
  },
  checkbox: {
    cursor: "pointer",
  },
  modalFooter: {
    borderTop: "1px solid #e5e7eb",
    padding: "14px 20px",
    display: "flex",
    // A minWidth-pinned primary button plus its cancel overflowed a 360px
    // footer once the label was translated.
    flexWrap: "wrap" as const,
    justifyContent: "flex-end",
    gap: 10,
  },
  saveButton: {
    minWidth: 160,
  },
} as const;

export default ManualBillingModal;
